import axios from "axios";
import { PDFDocument, PDFTextField, PDFCheckBox, PDFDropdown, PDFRadioGroup, PDFField } from "pdf-lib";
import { FormFieldDefinition, FormSection } from "../db/schema";

// Swaps a private form-templates S3 URL for a short-lived presigned GET URL.
// Non-S3 URLs (blob:, data:, server proxies) are returned unchanged.
export async function resolveFormPdfUrl(
  originalUrl: string | null | undefined,
  // Agency templates managed by staff are viewed through a staff endpoint.
  viewUrlEndpoint = "/api/forms/view-url"
): Promise<string> {
  if (!originalUrl) return "";
  if (!originalUrl.includes("police-narratives-ai-form-templates")) {
    return originalUrl;
  }
  try {
    const res = await axios.post(viewUrlEndpoint, { url: originalUrl });
    return res.data.url;
  } catch (err) {
    console.error("Failed to resolve form PDF URL", err);
    return "";
  }
}

export interface ParsedPdfResult {
  isFillable: boolean;
  pageCount: number;
  fields: FormFieldDefinition[];
}

/**
 * Parse a PDF file and extract AcroForm fields
 */
export async function parsePdfFormFields(file: File): Promise<ParsedPdfResult> {
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer, {
    ignoreEncryption: true,
  });

  const pageCount = pdfDoc.getPageCount();

  let fields: FormFieldDefinition[] = [];
  let isFillable = false;

  try {
    const form = pdfDoc.getForm();
    const pdfFields = form.getFields();

    if (pdfFields.length > 0) {
      isFillable = true;
      fields = pdfFields.map((field, index) => extractFieldInfo(field, index, pdfDoc));
    }
  } catch (error) {
    // PDF doesn't have a form or form is corrupted
    console.log("No AcroForm found or error parsing form:", error);
    isFillable = false;
  }

  return {
    isFillable,
    pageCount,
    fields,
  };
}

/**
 * Extract information from a single PDF field
 */
function extractFieldInfo(
  field: PDFField,
  index: number,
  pdfDoc: PDFDocument
): FormFieldDefinition {
  const name = field.getName();
  const fieldType = getFieldType(field);

  // Try to get field position from widgets
  const widgets = field.acroField.getWidgets();
  let rect = { x: 0, y: 0, width: 200, height: 24 };
  let page = 1;

  if (widgets.length > 0) {
    const widget = widgets[0];
    const rectangle = widget.getRectangle();

    // Get the page number for this widget by comparing dictionaries
    const widgetDict = widget.dict;
    const widgetPage = pdfDoc.getPages().findIndex(p => {
      const annots = p.node.Annots();
      if (!annots) return false;
      return annots.asArray().some(annot => {
        const annotDict = pdfDoc.context.lookup(annot);
        return annotDict === widgetDict;
      });
    });

    if (widgetPage >= 0) {
      page = widgetPage + 1;
    }

    // PDF coordinates are from bottom-left, convert to top-left
    const pageObj = pdfDoc.getPage(page - 1);
    const pageHeight = pageObj.getHeight();

    rect = {
      x: rectangle.x,
      y: pageHeight - rectangle.y - rectangle.height, // Flip Y coordinate
      width: rectangle.width,
      height: rectangle.height,
    };
  }

  // Generate a human-readable label from the field name
  const label = generateLabelFromName(name);

  // Check if field is required (if available)
  const isRequired = field.acroField.hasFlag(1 << 1); // Ff flag bit 2 = Required

  return {
    id: `field_${index}_${Date.now()}`,
    name,
    label,
    type: fieldType,
    required: isRequired,
    page,
    rect,
    order: index + 1,
    source: "acroform",
    // Add placeholder context based on field name
    additionalContext: generateAdditionalContext(name, fieldType),
  };
}

/**
 * Determine the field type from a PDF field
 */
function getFieldType(field: PDFField): FormFieldDefinition["type"] {
  if (field instanceof PDFTextField) {
    // Check if it's a multiline text field
    const isMultiline = field.acroField.hasFlag(1 << 12); // Ff flag bit 13 = Multiline
    return isMultiline ? "textarea" : "text";
  }
  if (field instanceof PDFCheckBox) {
    return "checkbox";
  }
  if (field instanceof PDFDropdown) {
    return "text";
  }
  if (field instanceof PDFRadioGroup) {
    return "text";
  }
  // Default to text for unknown types
  return "text";
}

/**
 * Generate a human-readable label from a field name
 * e.g., "officer_name" -> "Officer Name"
 *       "Text1" -> "Text 1"
 *       "incidentDate" -> "Incident Date"
 */
function generateLabelFromName(name: string): string {
  // Handle camelCase
  let result = name.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Handle snake_case and kebab-case
  result = result.replace(/[_-]/g, " ");

  // Handle "Text1" -> "Text 1"
  result = result.replace(/([a-zA-Z])(\d)/g, "$1 $2");

  // Capitalize first letter of each word
  result = result
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  return result.trim();
}

/**
 * Generate an AI hint based on field name and type
 */
function generateAdditionalContext(name: string, type: string): string | undefined {
  const nameLower = name.toLowerCase();

  // Common field name patterns
  if (nameLower.includes("name") && nameLower.includes("officer")) {
    return "Full name of the officer";
  }
  if (nameLower.includes("name") && (nameLower.includes("suspect") || nameLower.includes("subject"))) {
    return "Full name of the suspect/subject";
  }
  if (nameLower.includes("name") && nameLower.includes("victim")) {
    return "Full name of the victim";
  }
  if (nameLower.includes("badge") || nameLower.includes("id")) {
    return "Officer's badge or ID number";
  }
  if (nameLower.includes("date") && nameLower.includes("incident")) {
    return "Date when the incident occurred";
  }
  if (nameLower.includes("date")) {
    return "Relevant date";
  }
  if (nameLower.includes("time")) {
    return "Relevant time";
  }
  if (nameLower.includes("location") || nameLower.includes("address")) {
    return "Location or address where event occurred";
  }
  if (nameLower.includes("description") || nameLower.includes("narrative") || nameLower.includes("summary")) {
    return "Detailed description of what occurred";
  }
  if (nameLower.includes("phone") || nameLower.includes("contact")) {
    return "Contact phone number";
  }
  if (nameLower.includes("email")) {
    return "Email address";
  }
  if (type === "checkbox") {
    // Try to infer what the checkbox is for
    if (nameLower.includes("taser") || nameLower.includes("cew")) {
      return "Check if taser/CEW was used";
    }
    if (nameLower.includes("firearm") || nameLower.includes("gun") || nameLower.includes("weapon")) {
      return "Check if firearm was used";
    }
    if (nameLower.includes("force")) {
      return "Check if this type of force was used";
    }
  }

  return undefined;
}

/**
 * Check if a File is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

// ---------------------------------------------------------------------------
// AI field audit
// ---------------------------------------------------------------------------

export interface AiPageImage {
  page: number;
  imageBase64: string;
  widthPt: number;
  heightPt: number;
}

const AI_MAX_PAGES = 15;
// The API accepts 10MB of JSON; stay well under it.
const AI_MAX_BASE64_BYTES = 7 * 1024 * 1024;
// With a completed example along, the blank form and the example share the
// same request, so each gets a smaller slice of the 10MB.
export const AI_BLANK_WITH_REFERENCE_BYTES = 5 * 1024 * 1024;
export const AI_REFERENCE_BYTES = 3.5 * 1024 * 1024;

async function renderPages(
  pdf: any,
  pageCount: number,
  targetWidthPx: number,
  quality: number
): Promise<AiPageImage[]> {
  const out: AiPageImage[] = [];
  for (let n = 1; n <= pageCount; n++) {
    const page = await pdf.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: targetWidthPx / base.width });
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    out.push({
      page: n,
      imageBase64: dataUrl.split(",")[1],
      widthPt: base.width,
      heightPt: base.height,
    });
    page.cleanup();
  }
  return out;
}

/** Render each page (max 15) to a JPEG small enough to POST in one request. */
export async function renderPdfPagesForAi(
  source: File | ArrayBuffer,
  maxBytes: number = AI_MAX_BASE64_BYTES
): Promise<AiPageImage[]> {
  const { pdfjs } = await import("react-pdf");
  // react-pdf's default ("pdf.worker.mjs") does not resolve in this app; the
  // worker is served from /public. Set it here so this helper never depends
  // on another component having loaded first.
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  const data = source instanceof ArrayBuffer ? source : await source.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pageCount = Math.min(pdf.numPages, AI_MAX_PAGES);
  const attempts: Array<[number, number]> = [
    [1500, 0.8],
    [1300, 0.7],
    [1100, 0.6],
    [900, 0.5],
  ];
  let pages: AiPageImage[] = [];
  for (const [width, quality] of attempts) {
    pages = await renderPages(pdf, pageCount, width, quality);
    const total = pages.reduce((n, p) => n + p.imageBase64.length, 0);
    if (total <= maxBytes) break;
  }
  await pdf.destroy();
  return pages;
}

export interface AutoFieldsStats {
  existing: number;
  added: number;
  updated: number;
  flagged: number;
  // Present when a completed example was supplied. A failed example pass never
  // fails the run: the placed fields are kept and `applied` is false.
  reference?: { applied: boolean; refined?: number; error?: string };
}

export interface AutoFieldsProposal {
  fields: FormFieldDefinition[];
  sections: FormSection[];
  stats: AutoFieldsStats;
}

// Test hook: in the browser console, run
//   localStorage.setItem("pnai-force-form-ai-error", "1")
// and the next upload's field pass fails on purpose (the server ignores this in
// production). Remove the key to turn it off.
export const FORCE_AI_ERROR_KEY = "pnai-force-form-ai-error";
function shouldForceAiError(): boolean {
  try {
    return window.localStorage.getItem(FORCE_AI_ERROR_KEY) === "1";
  } catch {
    return false;
  }
}

// The officer cancelled the run from the locked page.
export class AiAuditCancelled extends Error {
  constructor() {
    super("Cancelled");
  }
}

// The server reported that the run failed. `removed` is true when the server
// already removed the form (a first upload whose fields could not be read).
export class AiAuditFailed extends Error {
  removed: boolean;
  constructor(message: string, removed: boolean) {
    super(message);
    this.removed = removed;
  }
}

// We can no longer see the job (its status expired or the page cannot reach the
// server), but it may well have finished. The caller must not treat this as a
// failure of the form and must not delete it.
export class AiAuditLost extends Error {
  constructor() {
    super("Lost track of the field analysis");
  }
}

const POLL_MS = 2000;
// Status checks that find no job (expired) or fail to reach the server, in a
// row, before we stop following. Roughly one minute either way.
const MAX_BLIND_POLLS = 30;

/**
 * Follow a running audit until the server says it is done. There is no
 * deadline: the work happens on the server, so this just watches for as long as
 * the tab stays open. It stops only when the server reports a result, or when
 * the job can no longer be seen for about a minute. Does not start anything.
 */
export async function waitForAiAudit(
  templateId: string,
  onProgress: ((done: number, total: number) => void) | undefined,
  apiBase = "/api/forms/templates"
): Promise<any> {
  let blind = 0;
  for (;;) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    let data: any;
    try {
      ({ data } = await axios.get(`${apiBase}/${templateId}/auto-fields`));
    } catch {
      // Offline for a moment, or a server restart: keep watching.
      if (++blind >= MAX_BLIND_POLLS) throw new AiAuditLost();
      continue;
    }
    if (data.status === "done") return data;
    if (data.status === "cancelled") throw new AiAuditCancelled();
    if (data.status === "error") throw new AiAuditFailed(data.error || "Field analysis failed", !!data.removed);
    if (data.status === "running") {
      blind = 0;
      onProgress?.(data.done ?? 0, data.total ?? 0);
    } else if (++blind >= MAX_BLIND_POLLS) {
      throw new AiAuditLost();
    }
  }
}

/**
 * Hand the pages to the server, which reads the form in the background and
 * saves the result to the template. Resolves as soon as the server has accepted
 * the job; follow it with waitForAiAudit. A failure here means the server never
 * took the job.
 */
export async function startAiFieldAudit(
  templateId: string,
  pages: AiPageImage[],
  apiBase = "/api/forms/templates",
  referencePages?: AiPageImage[]
): Promise<void> {
  await axios.post(`${apiBase}/${templateId}/auto-fields`, {
    pages,
    ...(referencePages?.length && { referencePages }),
    ...(shouldForceAiError() && { forceError: true }),
  });
}

/** A short, user-facing reason for a failed audit (server message if any). */
export function aiAuditErrorMessage(error: unknown): string {
  const e = error as any;
  return (
    e?.response?.data?.error ||
    (typeof e?.message === "string" ? e.message : "") ||
    "Unknown error"
  );
}
